import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { IconInfoCircle } from '@tabler/icons-react'

import { Progress } from '@/components/progress/Progress'
import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { Banner } from '@/components/banners/Banner'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import { Loader } from '@/components/loaders/Loader'
import { toast } from '@/components/toasts/toast.helpers'
import {
  fetchScorecardConfig,
  previewBulkRescore,
  submitBulkRescore,
} from '@/reducers/bulkScoring/bulkScoring.actions'
import { setData } from '@/reducers/bulkScoring/bulkScoring.redux'
import './BulkRescore.scss'

const MAX_DATE_RANGE_DAYS = 60
const MAX_DISPLAYED_ERRORS = 100
const SSE_THROTTLE_MS = 250

const JOB_STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
  RUNNING: 'running',
}

const CRITERIA_TYPE_AI = 'ai'

const TERMINAL_STATUSES = [JOB_STATUS.COMPLETED, JOB_STATUS.FAILED, JOB_STATUS.CANCELLED]
const ACTIVE_STATUSES = [JOB_STATUS.PENDING, JOB_STATUS.RUNNING]

const BulkRescoreTab = ({ selectedOrgId }) => {
  const dispatch = useDispatch()
  const { data: scorecardData, loading: scorecardLoading } = useSelector(
    (state) => state.scorecards
  )
  const { data: bulkData, loading: bulkLoading } = useSelector((state) => state.bulkScoring)

  const selectedConfig = bulkData.scorecardConfig
  const preview = bulkData.rescorePreview
  const jobStatus = bulkData.rescoreJobStatus

  const [selectedConfigId, setSelectedConfigId] = useState(null)
  const [selectedCriteria, setSelectedCriteria] = useState([])
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [dateInvalid, setDateInvalid] = useState(false)

  const abortControllerRef = useRef(null)
  const throttleTimerRef = useRef(null)
  const latestJobStatusRef = useRef(null)

  useEffect(() => {
    setSelectedConfigId(null)
    setSelectedCriteria([])
    dispatch(setData('rescorePreview', null))
    dispatch(setData('scorecardConfig', null))
  }, [selectedOrgId])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedConfigId) {
      dispatch(setData('scorecardConfig', null))
      setSelectedCriteria([])
      return
    }

    setSelectedCriteria([])
    dispatch(fetchScorecardConfig(selectedConfigId))
  }, [selectedConfigId])

  const throttledSetJobStatus = (data) => {
    const isTerminal = TERMINAL_STATUSES.includes(data.status)
    latestJobStatusRef.current = data

    if (isTerminal) {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
        throttleTimerRef.current = null
      }
      dispatch(setData('rescoreJobStatus', data))
      return
    }

    if (!throttleTimerRef.current) {
      dispatch(setData('rescoreJobStatus', data))
      throttleTimerRef.current = setTimeout(() => {
        throttleTimerRef.current = null
        if (latestJobStatusRef.current) {
          dispatch(setData('rescoreJobStatus', latestJobStatusRef.current))
        }
      }, SSE_THROTTLE_MS)
    }
  }

  const scorecardOptions = useMemo(() => {
    return (scorecardData.scorecardConfigs || [])
      .filter((scorecard) => !scorecard?.deleted)
      .map((scorecard) => ({ value: scorecard.id, label: scorecard.name, sid: scorecard.sid }))
  }, [scorecardData.scorecardConfigs])

  const criteriaOptions = useMemo(() => {
    if (!selectedConfig?.sections) {
      return []
    }

    const options = []
    selectedConfig.sections.forEach((section) => {
      if (!section.measures) {
        return
      }
      section.measures.forEach((measure) => {
        if (!measure.criteria) {
          return
        }
        measure.criteria.forEach((criterion) => {
          if (criterion.criteria_type === CRITERIA_TYPE_AI) {
            options.push({
              value: criterion.scid,
              label: criterion.name,
            })
          }
        })
      })
    })
    return options
  }, [selectedConfig])

  const selectedScorecardSid = useMemo(() => {
    if (!selectedConfigId) {
      return null
    }
    const match = scorecardOptions.find((option) => option.value === selectedConfigId)
    return match?.sid || null
  }, [selectedConfigId, scorecardOptions])

  const handleScorecardSelect = (option) => {
    setSelectedConfigId(option?.value || null)
    setSelectedCriteria([])
    dispatch(setData('rescorePreview', null))
  }

  const handleCriteriaSelect = (options) => {
    setSelectedCriteria(options || [])
    dispatch(setData('rescorePreview', null))
  }

  const selectedCriteriaScids = selectedCriteria.map((option) => option.value)

  const handleDateChange = ({ selection }) => {
    const start = moment(selection.startDate).startOf('day')
    const end = moment(selection.endDate).startOf('day')
    const diff = end.diff(start, 'days')

    setDateInvalid(diff >= MAX_DATE_RANGE_DAYS)
    setStartDate(selection.startDate)
    setEndDate(selection.endDate)
    dispatch(setData('rescorePreview', null))
  }

  const isJobActive = jobStatus && ACTIVE_STATUSES.includes(jobStatus.status)
  const canSubmit =
    selectedScorecardSid &&
    !isEmpty(selectedCriteriaScids) &&
    startDate &&
    endDate &&
    !dateInvalid &&
    !bulkLoading.rescoreSubmitting &&
    !isJobActive

  const getRequestParams = () => ({
    scorecard_config_sid: selectedScorecardSid,
    criteria_scids: selectedCriteriaScids,
    start_date: moment(startDate).startOf('day').toISOString(),
    end_date: moment(endDate).endOf('day').toISOString(),
  })

  const handlePreview = () => {
    if (!canSubmit) {
      return
    }
    dispatch(previewBulkRescore(getRequestParams()))
  }

  const handleConfirmSubmit = () => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    dispatch(
      submitBulkRescore(getRequestParams(), {
        abortController: controller,
        onProgress: throttledSetJobStatus,
      })
    ).then(() => {
      abortControllerRef.current = null
    })
  }

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      dispatch(
        setData(
          'rescoreJobStatus',
          jobStatus ? { ...jobStatus, status: JOB_STATUS.CANCELLED } : null
        )
      )
      toast.info('Disconnected — job may still be running on the server')
    }
  }

  const totalItems = Number(jobStatus?.total_items) || 0
  const processedItems = Number(jobStatus?.processed) || 0
  const progressPercent = totalItems > 0 ? Math.round((processedItems / totalItems) * 100) : 0

  const getStatusPill = () => {
    const statusMap = {
      [JOB_STATUS.COMPLETED]: { success: true },
      [JOB_STATUS.FAILED]: { critical: true },
      [JOB_STATUS.CANCELLED]: { caution: true },
      [JOB_STATUS.RUNNING]: { informative: true },
      [JOB_STATUS.PENDING]: { informative: true },
    }
    const props = statusMap[jobStatus.status] || {}
    return (
      <Pill small emphasized {...props}>
        {jobStatus.status}
      </Pill>
    )
  }

  return (
    <>
      <Banner informative icon={<IconInfoCircle />} header="How bulk rescoring works">
        <ul>
          <li>Select a scorecard config, then choose which AI criteria to rescore.</li>
          <li>
            Only existing scorecards within the date range will be rescored &mdash; no new
            scorecards are created.
          </li>
          <li>
            Use Preview to see how many scorecards and API calls will be made before starting the
            job.
          </li>
        </ul>
      </Banner>

      <Card title="Configuration">
        <div className="bulk-operations-form">
          <div className="form-field">
            <label>Scorecard Config</label>
            <Select
              options={scorecardOptions}
              value={selectedConfigId}
              onChange={handleScorecardSelect}
              placeholder="Select a scorecard config..."
              loading={scorecardLoading.scorecards}
              isClearable
            />
          </div>

          {bulkLoading.scorecardConfig && <Loader centered />}

          {selectedConfig && !bulkLoading.scorecardConfig && (
            <div className="form-field">
              <label>AI Criteria to Rescore</label>
              <MultiSelect
                options={criteriaOptions}
                value={selectedCriteria}
                onChange={handleCriteriaSelect}
                placeholder="Select criteria..."
                maxMenuHeight={200}
              />
              {isEmpty(criteriaOptions) && (
                <Banner caution className="mt">
                  No AI criteria found for this scorecard config.
                </Banner>
              )}
            </div>
          )}

          <div className="form-field">
            <label>Date Range (max {MAX_DATE_RANGE_DAYS} days)</label>
            <DateRangePickerReusable
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateChange}
              error={dateInvalid}
            />
            {dateInvalid && (
              <Banner critical className="mt">
                Date range must be {MAX_DATE_RANGE_DAYS} days or less.
              </Banner>
            )}
          </div>

          <Button
            primary
            onClick={handlePreview}
            disabled={!canSubmit}
            loading={bulkLoading.rescorePreview}
            data-testid="preview-rescore-btn"
          >
            Preview
          </Button>
        </div>
      </Card>

      {preview && (
        <Card title="Rescore Preview" className="bulk-operations-preview">
          <Banner informative icon={<IconInfoCircle />} header="Please confirm before proceeding">
            <div className="preview-details">
              <strong>Scorecards to rescore:</strong>{' '}
              <span data-testid="preview-total-scorecards">{preview.total_scorecards}</span>
              <br />
              <strong>Criteria per scorecard:</strong> {preview.total_criteria}
              <br />
              <strong>Total API calls:</strong> {preview.total_api_calls}
              <br />
              <strong>Estimated cost:</strong>{' '}
              <span data-testid="preview-estimated-cost">
                {preview.estimated_cost != null
                  ? `$${preview.estimated_cost.toFixed(2)}`
                  : 'N/A (no historical data)'}
              </span>
            </div>
            {preview.total_scorecards === 0 && (
              <p className="preview-warning">No scorecards found for this config and date range.</p>
            )}
          </Banner>

          {preview.total_scorecards > 0 && (
            <div className="preview-actions">
              <Button
                primary
                onClick={handleConfirmSubmit}
                disabled={bulkLoading.rescoreSubmitting || isJobActive}
                loading={bulkLoading.rescoreSubmitting}
                data-testid="confirm-rescore-btn"
              >
                Confirm &amp; Start Rescore
              </Button>
              <Button onClick={() => dispatch(setData('rescorePreview', null))}>Cancel</Button>
            </div>
          )}
        </Card>
      )}

      {jobStatus && (
        <Card title="Job Status" className="bulk-operations-status">
          <div className="status-details">
            <span className="status-label">Job ID:</span>{' '}
            <span data-testid="job-id">{jobStatus.job_id}</span>
            <br />
            <span className="status-label">Status:</span> {getStatusPill()}
            <br />
            <span className="status-label">Progress:</span> {jobStatus.processed} /{' '}
            {jobStatus.total_items}
            <br />
            <span className="status-label">Succeeded:</span> {jobStatus.succeeded}
            <br />
            <span className="status-label">Failed:</span> {jobStatus.failed}
          </div>

          <Progress
            value={progressPercent}
            success={jobStatus.status === JOB_STATUS.COMPLETED && jobStatus.failed === 0}
            warning={jobStatus.status === JOB_STATUS.COMPLETED && jobStatus.failed > 0}
            critical={jobStatus.status === JOB_STATUS.FAILED}
          />

          {jobStatus.status === JOB_STATUS.RUNNING && (
            <Button onClick={handleCancel} className="mt">
              Cancel Job
            </Button>
          )}

          {!isEmpty(jobStatus.errors) && (
            <div className="errors-container">
              <h4>Errors ({jobStatus.errors.length})</h4>
              <div className="errors-list">
                {jobStatus.errors.slice(0, MAX_DISPLAYED_ERRORS).map((error, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${index}-${error.scorecard_id || ''}-${error.error}`}
                    className="error-item"
                  >
                    {error.scorecard_id && <span>Scorecard {error.scorecard_id}: </span>}
                    {error.error}
                  </div>
                ))}
                {jobStatus.errors.length > MAX_DISPLAYED_ERRORS && (
                  <div className="errors-overflow">
                    and {jobStatus.errors.length - MAX_DISPLAYED_ERRORS} more...
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
    </>
  )
}

export default BulkRescoreTab
